import type {
  InfiniteQueryObserverOptions,
  UseInfiniteQueryResult,
  InfiniteData,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';

import { useInfiniteQuery as useReactInfiniteQuery } from '@tanstack/react-query';
import React from 'react';

import type { BaseApiPaginatedResponse } from '../../api/types/api.types';

const EMPTY_ARRAY: any[] = [];

type InferReturnType<TQueryFnData> =
  InfiniteData<TQueryFnData>['pages'][number] extends InfiniteData<TQueryFnData>['pages'][number] & {
    data: infer D;
  }
    ? { total: number } & D
    : InfiniteData<TQueryFnData>['pages'][number];
/**
 * What this hook ACTUALLY returns: every flag React Query reports, with `data`
 * replaced by the flattened list — and `data` is always present.
 *
 * Deliberately not `UseInfiniteQueryResult<…>`. That is the union in which
 * `data` is `undefined` on every branch except success, and this hook defaults
 * it to `{ data: [], total: 0 }` instead, so no member of that union ever
 * described the object below. `Omit` over the union keeps the flags (each typed
 * as the union of its per-branch types) and drops the one property whose
 * contract differs.
 *
 * `TData` is carried through rather than fixed to `InfiniteData<TQueryFnData>`:
 * the observer parameterises `fetchNextPage`/`refetch` by it, so pinning it
 * here would make those return types disagree with the ones the caller gets.
 * Pinned by `__tests__/infinite-query-data-shape.spec.tsx`.
 */
type InfiniteListResult<TQueryFnData, TError, TData> = Omit<
  UseInfiniteQueryResult<TData, TError>,
  'data'
> & {
  data: {
    data: InferReturnType<TQueryFnData>;
    total: number;
    /**
     * Search quality Phase 2: the FIRST page's correction of a search that
     * fell back to typo/inflection alternatives. `''` = corrected, no safe
     * words (generic notice); absent = no correction. Never taken from a
     * later page, and absent while a changed term's previous page stands in
     * as placeholder data.
     */
    correctedQuery?: string;
  };
};

/**
 * Serves as base query for fetching data
 */
export default function useInfiniteQuery<
  TQueryFnData,
  TError = unknown,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = string,
>(
  options: Omit<
    InfiniteQueryObserverOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryFnData,
      TQueryKey,
      TPageParam
    >,
    'getNextPageParam'
  > & { meta: { limit: string } },
  queryClient?: QueryClient,
): InfiniteListResult<TQueryFnData, TError, TData> {
  const memoizedArgs = React.useMemo(
    () => ({
      ...options,
      getNextPageParam: (
        lastPage: TQueryFnData,
        _: unknown,
        lastPageParam: TPageParam,
      ) => {
        const total = (lastPage as { total: number })?.total ?? 0;
        const limit = parseInt(options.meta.limit, 10);
        const totalPages = Math.ceil(total / limit);
        const allPages = Array.from(
          { length: totalPages },
          (_, i) => `${i + 1}`,
        );
        const index = allPages.findIndex((page) => page === lastPageParam);
        const nextPage = allPages?.[index + 1] || lastPageParam;
        if (nextPage === lastPageParam) return undefined;
        return nextPage as TPageParam;
      },
      placeholderData: (
        previous: InfiniteData<TQueryFnData, TPageParam> | undefined,
      ) => previous,
    }),
    [options],
  );
  const { error, ...other } = useReactInfiniteQuery(memoizedArgs, queryClient);

  // Every list endpoint answers `BaseApiPaginatedResponse` pages; `TData` stays
  // generic only for the observer's `fetchNextPage`/`refetch` types.
  const pages = (
    other.data as InfiniteData<BaseApiPaginatedResponse<TData[]>> | undefined
  )?.pages;

  const data = React.useMemo(
    () =>
      (pages?.flatMap((page) => page.data) ??
        EMPTY_ARRAY) as InferReturnType<TQueryFnData>,
    [pages],
  );
  const total = React.useMemo(() => pages?.[0]?.total ?? 0, [pages]);

  const correctedQuery = other.isPlaceholderData
    ? undefined
    : pages?.[0]?.correctedQuery;

  const wrapped = React.useMemo(
    () => ({
      data,
      total,
      ...(correctedQuery !== undefined && { correctedQuery }),
    }),
    [data, total, correctedQuery],
  );
  return {
    error,
    ...other,
    data: wrapped,
  };
}
